import React, { useCallback, useState, useEffect, useMemo, useRef, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import _ from 'lodash';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { bindActionCreators } from 'redux';
import { useTranslation } from "react-i18next";
import {
    makeStyles,
    useTheme,
    useMediaQuery,
    Typography,
    Grid,
    Hidden,
} from "@material-ui/core";
import { CertificateFields, CovidVaccine, InfluenzaVaccine } from '@components/organisms';

import { Button, CircularLoader } from '@components/atoms';
import { DialogConfirm, Snackbar } from '@components/molecules';

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimesCircle } from "@fortawesome/free-solid-svg-icons";

// import { certiticateFields, imageFields, ADD_CERTIFICATE_FORM } from './constants';

//API Requests
import { CredentialsService } from "@services";
//Actions
import { setFilterValues, setCertificateTypes } from '@store/addCertificate';
import { saveCertificateData } from '@store/certificates';
import { connect } from 'react-redux';
import { formatAddUpdateCertificateRequestPayload, sleep } from './helperFunctions';
import validate from './validate';
import EcardModal from './EcardModal';

const useStyles = makeStyles(theme => ({
    titleCta: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    root: {
        width: '100%',
    },
    form: {
        width: '100%',
    },
    imageWrapper: {
        marginTop: theme.spacing(3),

    },
    actionButtons: {
        marginTop: theme.spacing(4),

    },
    label: {
        textTransform: 'capitalize',
    },
    topButtons: {
        display: 'flex',
        flexDirection: 'row',
    },
    closeButton: {
        display: 'flex',
        justifyContent: 'flex-end',
        cursor: 'pointer',
    },
    closeIconColor: {
        color: theme.palette.icon.primary,
    },
}));

const AddCertificates = forwardRef((props, ref) => {
    const { filterValues, certificateTypes, certificates, selectedCertImageData, addCertificate = true, actions: { setFilterValues, saveCertificateData, setCertificateTypes }, certificateDetails, user, handleClose } = props;
    const [hash, setHash] = useState('');
    const [state, setState] = useState({ loading: true, openSnackbar: false, submitMessage: '', selectedCertificate: null });
    const [openModal, setOpenModal] = useState(false);
    const [eCardData, setECardData] = useState(null);
    const [isFrontImageRequired, setIsFrontImageRequired] = useState(false);
    const [isBackImageRequired, setIsBackImageRequired] = useState(false);
    const [isChangingImage, setIsChangingImage] = useState(false);
    const [requiredImage, setRequiredImage] = useState(false);
    const [isLoadingOnSubmit, setIsLoadingOnSubmit] = useState(false);
    const eCardDataUpdatedRef = useRef(null);

    const { t } = useTranslation();
    const classes = useStyles();
    const covidCertRef = useRef(null);
    const theme = useTheme();
    const isSmUp = useMediaQuery(theme.breakpoints.up('sm'), {
        defaultMatches: true,
    });
    const isMdUp = useMediaQuery(theme.breakpoints.up('md'), {
        defaultMatches: true,
    });
    const onHashChange = useCallback(
        () => {
            const activeHash = window.location.hash ? window.location.hash.replace('#', '') : '';
            setHash(activeHash);
        },
        [],
    );

    const getFilterValues = async () => {
        const filterValues = await CredentialsService.getFilterValues(user.employeeNumber);
        const certificateTypes = await CredentialsService.getCertificatesTypes();
        setFilterValues(filterValues);
        setCertificateTypes(certificateTypes);
        const filteredCerts = removeAlreadyAddedCerts(filterValues.certificateTypes);
        const certificateTypesOptions = createCertificatesTypesOptions(addCertificate && certificates ? filteredCerts : certificateTypes);
        const agencyOptions = createAgencyOptions(filterValues.agencies);
        setState(prevState => ({ ...prevState, filteredCerts, certificateTypesOptions, agencyOptions, loading: false }));
    };

    const removeAlreadyAddedCerts = useCallback((certificateTypes) => {
        if (!certificates) {
            return null;
        }
        const { allowedDup, notAllowedDup } = certificateTypes.reduce((acc, cert) => {
            if (cert.isAllowDuplicates === "True") acc.allowedDup.push(cert)
            else acc.notAllowedDup.push(cert);
            return acc;
        }, { allowedDup: [], notAllowedDup: [] });
        const certificateIds = certificates.filter(cert => !['MISSING', 'REJECTED'].includes(cert.disposition)).map(addedCert => addedCert.certificateID);
        const removedAlreadyAddedCerts = notAllowedDup.reduce((acc, nAllowedcert) => {
            if (!certificateIds.includes(nAllowedcert.certificateID)) acc.push(nAllowedcert); return acc;
        }, []);
        const filteredCerts = [...allowedDup, ...removedAlreadyAddedCerts].sort((a, b) => _.toLower(a.certificateName) < _.toLower(b.certificateName) ? -1 : _.toLower(a.certificateName) > _.toLower(b.certificateName) ? 1 : 0); // _.sortBy([...allowedDup, ...removedAlreadyAddedCerts], ['certificateName']);
        return filteredCerts;
    });

    const createCertificatesTypesOptions = useCallback((certificateTypes) => { return certificateTypes.map(v => ({ label: v.certificateName, value: v.certificateID })) }, [filterValues]);

    const createAgencyOptions = useCallback(agencies => { return _.sortBy(agencies.map(v => ({ label: v.agencyDescription, value: v.agencyDescription })), ['label']); }, [filterValues]);

    useEffect(() => {
        //Event for handling browser back button or Front and back handling
        window.addEventListener('hashchange', onHashChange);
        window.addEventListener('popstate', onHashChange);
        onStepChange(1);
        try {
            if (!filterValues) {
                setState(prevState => ({ ...prevState, loading: true }));
                getFilterValues();
            } else {
                const agencyOptions = createAgencyOptions(filterValues.agencies);
                const filteredCerts = removeAlreadyAddedCerts(filterValues.certificateTypes);
                setState(prevState => ({ ...prevState, filteredCerts, certificateTypesOptions: createCertificatesTypesOptions(filteredCerts), agencyOptions, loading: false }));
            }

        } catch (error) {
            setState(prevState => ({ ...prevState, loading: false }));
        }
        return () => {
            window.removeEventListener('hashchange', onHashChange);
            window.removeEventListener('popstate', onHashChange);
            onStepChange('');
        }
    }, []);

    useEffect(() => {
        if (filterValues?.certificateTypes && certificates?.length) {
            const filteredCerts = removeAlreadyAddedCerts(filterValues.certificateTypes);
            setState(prevState => ({ ...prevState, filteredCerts, certificateTypesOptions: createCertificatesTypesOptions(addCertificate ? filteredCerts : certificateTypes) }));
        }
    }, [certificates]);

    const onStepChange = (stepNo) => window.location.hash = Number(stepNo) ? `step-${stepNo}` : '';

    const formikInitialValues = useMemo(() => ({
        certificateID: addCertificate ? '' : certificateDetails?.certificateID || '',
        documentNumber: addCertificate ? '' : certificateDetails?.documentNumber || '',
        agency: addCertificate ? '' : certificateDetails?.agency || '',
        stateCode: addCertificate ? '' : certificateDetails?.stateCode || '',
        countyName: addCertificate ? '' : certificateDetails?.countyName || '',
        jobTitle: addCertificate ? '' : certificateDetails?.jobTitle || '',
        issueDate: '', // addCertificate ? '' : certificateDetails?.issueDate || '',
        expirationDate: '', // addCertificate ? '' : certificateDetails?.expirationDate || '',
        frontImage: addCertificate ? '' : selectedCertImageData?.frontImageType ? [{ original: `data:${selectedCertImageData.frontImageType};base64,` + selectedCertImageData.frontImage }] : '',
        backImage: addCertificate ? '' : selectedCertImageData?.backImageType ? [{ original: `data:${selectedCertImageData.backImageType};base64,` + selectedCertImageData.backImage }] : '',
        certificateImage: '', //addCertificate ? '' : selectedCertImageData?.certificateType ? [{ original: `data:${selectedCertImageData.certificateType};base64,` + selectedCertImageData.certificateImage }] : '',
        manufacturer: addCertificate ? '' : _.toLower(certificateDetails?.manufacturer || ''),
        lotNumber: addCertificate ? '' : certificateDetails?.lotNumber || '',
        isVaccineDeclined: addCertificate ? false : certificateDetails?.isVaccineDeclined === "1" || false,
        vaccineBooster: addCertificate ? false : certificateDetails?.doseNumber === 'Yes' || false,
        activeFlag: addCertificate ? 'Active' : certificateDetails?.activeFlag || 'Active',
        // certificateDispositionID: certificateDetails?.certificateDispositionID || '',
        employeeCertificatesID: addCertificate ? '' : certificateDetails?.employeeCertificatesID || '',
        disposition: addCertificate ? '' : certificateDetails?.disposition || '',
        zipCode: addCertificate ? '' : certificateDetails?.zipCode || '',

    }), [addCertificate, certificateDetails, selectedCertImageData]);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: formikInitialValues,
        validate: (values) => validate(values, state, addCertificate ? filterValues : { certificateTypes }, addCertificate, eCardData),
        onSubmit: (values, rest) => {
            if (!values.isVaccineDeclined && values.eCardData && isChangingImage) {
                if (!values.frontImage && isFrontImageRequired) {
                    return setRequiredImage(true);
                }
                if (!values.backImage && isBackImageRequired) {
                    return setRequiredImage(true);
                }
            }
            if (Number(values.certificateID) === 1007) {
                const result = covidCertRef.current.validateAndDisplayBoosterAlert();
                if (!result) return;
            }
            const payload = formatAddUpdateCertificateRequestPayload({ values, compState: state, user, addCertificate, certificateDetails, selectedCertImageData });
            setIsLoadingOnSubmit(true);
            CredentialsService.addUpdateCertificates(payload).then(async (response) => {
                setECardData();
                setIsLoadingOnSubmit(false);
                setState(prevState => ({ ...prevState, selectedCertificate: null, openSnackbar: true, submitMessage: `Certificate ${addCertificate ? 'saved' : 'updated'} successfully!` }));
                if (addCertificate) {
                    onStepChange(1);
                    formik.resetForm({});
                    CredentialsService.getCerts(user?.employeeNumber)
                        .then(certs => {
                            saveCertificateData(certs);
                        });
                } else {
                    await sleep(2000);
                    onStepChange('');
                    window.location.reload();
                    handleClose();
                }
            }).catch(error => {
                setState(prevState => ({ ...prevState, openSnackbar: !prevState.openSnackbar, submitMessage: 'Error while saving certificate!', setIsLoadingOnSubmit: !prevState.setIsLoadingOnSubmit }));
            });
        }
    });

    const resetClickHandler = useCallback(() => {
        setECardData();
        setState(prevState => ({ ...prevState, selectedCertificate: null }));
        formik.resetForm({}); //this will not clear the fields instead reset the form to initial state,
        eCardDataUpdatedRef.current = false;
    }, []);

    const renderCertificateBasedOnName = () => {
        const obj = {
            isSmUp, hash, filterValues, state, setState, formik, setOpenModal, addCertificate,
            eCardData, setECardData, setIsFrontImageRequired, setIsBackImageRequired, isChangingImage, setIsChangingImage,
            certificates, certificateTypes, eCardDataUpdatedRef
        };
        if (Number(formik.values.certificateID) === 1007) return <CovidVaccine {...{ ...obj, ref: covidCertRef }} />;
        if (Number(formik.values.certificateID) === 259) return <InfluenzaVaccine {...{ ...obj }} />

        return <CertificateFields {...{ ...obj }} />;
    };

    const handleCloseSnackbar = () => setState(prevState => ({ ...prevState, openSnackbar: !prevState.openSnackbar }));

    useImperativeHandle(
        ref,
        () => ({
            getFormikObject: () => formik,
        }), [formik]);

    // console.log('formik.errors', formik.errors, 'formik.values', formik.values, 'formik.touched', formik.touched);
    // console.table(user);
    if (state.loading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}><CircularLoader /></div>;
    return (
        <>
            {!addCertificate && (
                <div className={classes.closeButton} onClick={handleClose}>
                    <FontAwesomeIcon
                        icon={faTimesCircle}
                        size="2x"
                        className={classes.closeIconColor}
                    />
                </div>
            )}
            <Grid container flex justifyContent={"space-between"} className={classes.root}>
                <form onSubmit={formik.handleSubmit} className={classes.form}>
                    {
                        addCertificate && (
                            <Grid item container>
                                <Grid item container flex justifyContent="flex-end">
                                    <Button onClick={resetClickHandler} variant="outlined" className={classes.label}>Clear Fields</Button>
                                </Grid>
                            </Grid>
                        )
                    }
                    <Grid item container flex justifyContent={"space-between"} >
                        {renderCertificateBasedOnName()}
                    </Grid>
                    <Grid container direction="row" justifyContent="center" alignItems="center" className={classes.actionButtons}>
                        <Grid item container md={4} style={{ justifyContent: addCertificate && hash.split('-').pop() !== '1' ? "center" : "space-between" }}>
                            <Hidden smUp>{hash.split('-').pop() > '1' && (<Button onClick={() => onStepChange(1)} variant="outlined">Previous</Button>)}</Hidden>
                            <Button type="submit" disabled={isLoadingOnSubmit}>
                                {!isLoadingOnSubmit ?
                                    'Submit'
                                    : <CircularLoader color="white" size={25} />
                                }
                            </Button>
                            {!addCertificate && (
                                <Hidden smDown><Button variant="outlined" onClick={handleClose}>Cancel</Button></Hidden>
                            )}
                            <Hidden smUp>{hash.split('-').pop() === '1' && (<Button onClick={() => onStepChange(2)}>Next</Button>)}</Hidden>
                        </Grid>
                    </Grid>
                </form>
                <Snackbar
                    open={state.openSnackbar}
                    alertMessage={state.submitMessage}
                    onClose={handleCloseSnackbar}
                    alertProps={{ severity: state.submitMessage?.includes('success') ? "success" : "error" }}
                    autoHideDuration={2000}
                />
            </Grid>
            {openModal && (
                <EcardModal
                    openModal={openModal}
                    setOpenModal={setOpenModal}
                    setECardData={setECardData}
                    formik={formik}
                    addCertificate={addCertificate}
                    eCardDataUpdatedRef={eCardDataUpdatedRef}
                    certificateTypesOptions={state.certificateTypesOptions}
                />
            )}
            <DialogConfirm {...{
                dialogProps: {
                    open: requiredImage,
                },
                title: 'Missing Required Image(s)',
                content: 'Please upload required images.',
                onOkClickHandler: () => setRequiredImage(false),
                onCancelClickHandler: () => setRequiredImage(false)
            }}
            />
        </>
    );
});

AddCertificates.propTypes = {
    /**
     * External classes
     */
    className: PropTypes.string,

};

const mapStateToProps = (state) => {
    return {
        filterValues: state.addCertificate.filterValues,
        certificateTypes: state.addCertificate.certificateTypes,
        selectedCertImageData: state.certificates.selectedCertImageData,
        user: state.user.user,
        certificates: state.certificates.certificates,
    }
};

const mapDispatchToProps = dispatch => ({
    dispatch,
    actions: bindActionCreators({ setFilterValues, saveCertificateData, setCertificateTypes }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true })(AddCertificates);
